import type { SegmentedControlLabeledIconSegment } from '@evisort/eds/components/SegmentedControl/types';

import { Layout } from '@evisort/eds/components/Layout/Layout';
import { Modal } from '@evisort/eds/components/Modal/Modal';
import { SegmentedControl } from '@evisort/eds/components/SegmentedControl/SegmentedControl';
import { Text } from '@evisort/eds/components/Text/Text';
import { useToast } from '@evisort/eds/components/Toast/hooks';
import { useForm, useStore } from '@tanstack/react-form';
import { defineMessages, useIntl } from 'react-intl';
import { useLocation, useMatches } from 'react-router';

import { FormField } from '~/eds/src/components/FormField/FormField';
import { TextArea } from '~/eds/src/components/TextArea/TextArea';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { useFormFocusManager } from '~/hooks/useFormFocusManager';

import {
  type FeedbackSentiment,
  type FeedbackTopic,
  submitCLMEvolutionFeedback,
} from './submitCLMEvolutionFeedback';
import { toRouteTemplate } from './toRouteTemplate';

const messages = defineMessages({
  title: {
    id: 'CLMEvolutionFeedback.title',
    defaultMessage: 'Share Your Feedback',
    description: 'Title of the CLM Evolution feedback modal.',
  },
  description: {
    id: 'CLMEvolutionFeedback.description',
    defaultMessage:
      'Tell us what works well and what we should fix. Your feedback goes straight to the product team.',
    description: 'Description shown above the CLM Evolution feedback form.',
  },
  feedbackLabel: {
    id: 'CLMEvolutionFeedback.feedbackLabel',
    defaultMessage: 'What would you like to share?',
    description: 'Label for the CLM Evolution feedback field.',
  },
  feedbackPlaceholder: {
    id: 'CLMEvolutionFeedback.feedbackPlaceholder',
    defaultMessage: 'Describe your experience in detail',
    description: 'Placeholder for the CLM Evolution feedback field.',
  },
  feedbackRequired: {
    id: 'CLMEvolutionFeedback.feedbackRequired',
    defaultMessage: 'Enter your feedback before sending.',
    description:
      'Validation message when the CLM Evolution feedback field is empty.',
  },
  sentimentLabel: {
    id: 'CLMEvolutionFeedback.sentimentLabel',
    defaultMessage: 'Overall Experience',
    description: 'Label for the optional feedback sentiment picker.',
  },
  sentimentPositive: {
    id: 'CLMEvolutionFeedback.sentimentPositive',
    defaultMessage: 'Positive',
    description: 'Sentiment option for feedback about something that works.',
  },
  sentimentNeutral: {
    id: 'CLMEvolutionFeedback.sentimentNeutral',
    defaultMessage: 'Neutral',
    description: 'Sentiment option for feedback that is neither good nor bad.',
  },
  sentimentNegative: {
    id: 'CLMEvolutionFeedback.sentimentNegative',
    defaultMessage: 'Negative',
    description:
      'Sentiment option for feedback about a frustrating experience.',
  },
  topicLabel: {
    id: 'CLMEvolutionFeedback.topicLabel',
    defaultMessage: 'Topic',
    description: 'Label for the optional feedback topic picker.',
  },
  topicBug: {
    id: 'CLMEvolutionFeedback.topicBug',
    defaultMessage: 'Bug',
    description: 'Feedback topic option for something that is broken.',
  },
  topicIdea: {
    id: 'CLMEvolutionFeedback.topicIdea',
    defaultMessage: 'Idea',
    description: 'Feedback topic option for a suggestion or request.',
  },
  topicQuestion: {
    id: 'CLMEvolutionFeedback.topicQuestion',
    defaultMessage: 'Question',
    description: 'Feedback topic option for something that is unclear.',
  },
  topicOther: {
    id: 'CLMEvolutionFeedback.topicOther',
    defaultMessage: 'Other',
    description: 'Feedback topic option for anything else.',
  },
  note: {
    id: 'CLMEvolutionFeedback.note',
    defaultMessage: 'We read every piece of feedback.',
    description:
      'Reassurance shown at the end of the CLM Evolution feedback form.',
  },
  cancel: {
    id: 'CLMEvolutionFeedback.cancel',
    defaultMessage: 'Cancel',
    description: 'Action that dismisses the CLM Evolution feedback modal.',
  },
  submit: {
    id: 'CLMEvolutionFeedback.submit',
    defaultMessage: 'Send Feedback',
    description: 'Primary action that sends the CLM Evolution feedback.',
  },
  success: {
    id: 'CLMEvolutionFeedback.success',
    defaultMessage: 'Feedback sent',
    description: 'Toast shown after CLM Evolution feedback is sent.',
  },
});

interface CLMEvolutionFeedbackModalProps {
  isVisible: boolean;
  onHide: () => void;
}

export function CLMEvolutionFeedbackModal({
  isVisible,
  onHide,
}: CLMEvolutionFeedbackModalProps) {
  const intl = useIntl();
  const user = useCurrentUser();
  const { toast } = useToast();
  const { pathname } = useLocation();
  // The modal lives in the nav, above the routed page, so `useParams` would only
  // see the layout route's (empty) params — the deepest match has the real ones.
  const matches = useMatches();
  const routeParams = matches[matches.length - 1]?.params ?? {};

  const sentimentSegments: SegmentedControlLabeledIconSegment[] = [
    {
      icon: { key: 'like-thumb-up' },
      label: intl.formatMessage(messages.sentimentPositive),
      segmentKey: 'positive',
      text: intl.formatMessage(messages.sentimentPositive),
    },
    {
      icon: { key: 'minus-remove' },
      label: intl.formatMessage(messages.sentimentNeutral),
      segmentKey: 'neutral',
      text: intl.formatMessage(messages.sentimentNeutral),
    },
    {
      icon: { key: 'dislike-thumb-down' },
      label: intl.formatMessage(messages.sentimentNegative),
      segmentKey: 'negative',
      text: intl.formatMessage(messages.sentimentNegative),
    },
  ];

  const topicSegments: SegmentedControlLabeledIconSegment[] = [
    {
      icon: { key: 'warning-exclamation' },
      label: intl.formatMessage(messages.topicBug),
      segmentKey: 'bug',
      text: intl.formatMessage(messages.topicBug),
    },
    {
      icon: { key: 'help' },
      label: intl.formatMessage(messages.topicQuestion),
      segmentKey: 'question',
      text: intl.formatMessage(messages.topicQuestion),
    },
    {
      icon: { key: 'ai-sparkle' },
      label: intl.formatMessage(messages.topicIdea),
      segmentKey: 'idea',
      text: intl.formatMessage(messages.topicIdea),
    },
    {
      icon: { key: 'more' },
      label: intl.formatMessage(messages.topicOther),
      segmentKey: 'other',
      text: intl.formatMessage(messages.topicOther),
    },
  ];

  const form = useForm({
    defaultValues: {
      feedback: '',
      sentiment: null as FeedbackSentiment | null,
      topics: [] as FeedbackTopic[],
    },
    onSubmit: async ({ value }) => {
      submitCLMEvolutionFeedback({
        message: value.feedback.trim(),
        page: toRouteTemplate(pathname, routeParams),
        name: `${user.first_name} ${user.last_name}`.trim() || undefined,
        email: user.email || undefined,
        sentiment: value.sentiment ?? undefined,
        topics: value.topics.length ? value.topics : undefined,
      });
      toast({
        text: intl.formatMessage(messages.success),
        status: 'positive',
      });
      form.reset();
      onHide();
    },
  });

  useFormFocusManager(form);

  const isSubmitting = useStore(form.store, (state) => state.isSubmitting);
  const submissionAttempts = useStore(
    form.store,
    (state) => state.submissionAttempts,
  );

  const cancelLabel = intl.formatMessage(messages.cancel);
  const submitLabel = intl.formatMessage(messages.submit);

  return (
    <Modal
      isVisible={isVisible}
      header={{
        title: intl.formatMessage(messages.title),
      }}
      footer={{
        actions: [
          {
            label: cancelLabel,
            text: cancelLabel,
            variant: 'tertiary',
            onClick: onHide,
          },
          {
            disabled: isSubmitting,
            icon: { key: 'send', position: 'left' },
            isLoading: isSubmitting,
            label: submitLabel,
            text: submitLabel,
            variant: 'primary',
            onClick: () => {
              form.handleSubmit().catch(() => {});
            },
          },
        ],
      }}
      onHide={onHide}
    >
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          form.handleSubmit().catch(() => {});
        }}
      >
        <Layout direction="column" spacing="l">
          {/* Body copy rather than the header's `subtitle`, which EDS truncates
              to a single line. */}
          <Text as="p" tone="subdued" variant="body-m">
            {intl.formatMessage(messages.description)}
          </Text>
          <form.Field
            name="feedback"
            validators={{
              onSubmit: ({ value }) =>
                value.trim()
                  ? undefined
                  : intl.formatMessage(messages.feedbackRequired),
              onChange: ({ value }) =>
                value.trim()
                  ? undefined
                  : intl.formatMessage(messages.feedbackRequired),
            }}
          >
            {(field) => (
              <FormField<string, false>
                input={TextArea}
                label={intl.formatMessage(messages.feedbackLabel)}
                required
                name={field.name}
                placeholder={intl.formatMessage(messages.feedbackPlaceholder)}
                value={field.state.value}
                error={
                  submissionAttempts > 0
                    ? (field.state.meta.errors[0] ?? undefined)
                    : undefined
                }
                inputProps={{ enableAutoResize: true, rows: 4 }}
                onChange={(value) => field.handleChange(value ?? '')}
              />
            )}
          </form.Field>
          <form.Field name="sentiment">
            {(field) => (
              <Layout direction="column" spacing="s">
                <Text as="p" variant="body-semi-s">
                  {intl.formatMessage(messages.sentimentLabel)}
                </Text>
                <SegmentedControl
                  fullWidth
                  label={intl.formatMessage(messages.sentimentLabel)}
                  segments={sentimentSegments}
                  value={field.state.value ?? ''}
                  onChange={(segmentKey) =>
                    field.handleChange(segmentKey as FeedbackSentiment)
                  }
                />
              </Layout>
            )}
          </form.Field>
          <form.Field name="topics">
            {(field) => (
              <Layout direction="column" spacing="s">
                <Text as="p" variant="body-semi-s">
                  {intl.formatMessage(messages.topicLabel)}
                </Text>
                <SegmentedControl
                  fullWidth
                  label={intl.formatMessage(messages.topicLabel)}
                  segments={topicSegments}
                  selectionMode="multiple"
                  value={field.state.value}
                  onChange={(segmentKeys) =>
                    field.handleChange(segmentKeys as FeedbackTopic[])
                  }
                />
              </Layout>
            )}
          </form.Field>
          <Text as="p" tone="subdued" variant="body-s">
            {intl.formatMessage(messages.note)}
          </Text>
        </Layout>
      </form>
    </Modal>
  );
}
